import { Component, TemplateFunction } from '@ribajs/core'
import {
  ShopifyProductRecommendationsService,
  ShopifyProductService,
} from '../services'
import { TwSlideshowComponent } from '../modules/tailwind/components'

import type { ShopifyProduct, ShopifyCartLineItem } from '../types'
import type { JsxSbCartRecommendationsProps } from '../types/jsx'
import type { SlideshowSlide } from '../modules/tailwind/types'

interface Scope {
  items: ShopifyCartLineItem[]
  recommendations: ShopifyProduct[]
  slides: SlideshowSlide<ShopifyProduct>[]

  see56CollectionHandle?: string | undefined
  see56VariantIds?: string | undefined
}

let see56Enabled = false

window.addEventListener('see56Enable', () => {
  see56Enabled = true
})

/**
 * A component to get the product recommendations for each line item in the cart
 */
export class SbCardRecommendationsComponent extends Component {
  public static tagName = 'sb-cart-recommendations'

  public slideshow?: TwSlideshowComponent

  static get observedAttributes(): (keyof JsxSbCartRecommendationsProps)[] {
    return ['items', 'see-56-collection-handle', 'see-56-variant-ids']
  }

  protected requiredAttributes(): string[] {
    return ['items']
  }

  public scope: Scope = {
    items: new Array<ShopifyCartLineItem>(),
    recommendations: new Array<ShopifyProduct>(),
    slides: new Array<SlideshowSlide<ShopifyProduct>>(),
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCardRecommendationsComponent.observedAttributes)
    window.addEventListener('see56Enable', this.#onSee56Enable)
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('see56Enable', this.#onSee56Enable)
  }

  #onSee56Enable = () => {
    this.#getSee56Recommendations()
  }

  protected async afterBind() {
    await super.afterBind()
    this.slideshow =
      this.querySelector(TwSlideshowComponent.tagName) || undefined
    this.classList.add(`${SbCardRecommendationsComponent.tagName}-ready`)
  }

  protected updateSlides() {
    const slides = new Array<SlideshowSlide<ShopifyProduct>>()
    for (let i = 0; i < this.scope.recommendations.length; i++) {
      const recommendation = this.scope.recommendations[i]
      slides.push({
        active: false,
        index: i,
        data: recommendation,
      })
    }
    this.scope.slides = slides
    this.slideshow?.updateItems()
  }

  protected async getRecommendations() {
    if (this.scope.items?.length) {
      const productIds = this.scope.items.map(item => item.product_id) || []
      this.scope.recommendations =
        await ShopifyProductRecommendationsService.get(productIds, 8)
    } else {
      this.scope.recommendations = []
    }

    this.updateSlides()
  }

  async #getSee56Recommendations() {
    const collectionHandle = this.scope.see56CollectionHandle
    if (!collectionHandle) {
      return
    }

    const variantIds = new Set(
      (this.scope.see56VariantIds || '')
        .split(',')
        .map(item => Number(item.trim())),
    )

    const collection = await ShopifyProductService.collection(collectionHandle)
    if (!collection) {
      this.scope.recommendations = []
      this.updateSlides()
      return
    }

    const products = collection.collection.products
      .map(product => {
        return {
          ...product,
          variants: product.variants.filter(variant =>
            variantIds.has(variant.id),
          ),
        }
      })
      .filter(product => product.variants.length > 0)

    this.scope.recommendations = products
    this.updateSlides()
  }

  protected parsedAttributeChangedCallback(
    attributeName: string,
    oldValue: any,
    newValue: any,
    namespace: string | null,
  ) {
    super.parsedAttributeChangedCallback(
      attributeName,
      oldValue,
      newValue,
      namespace,
    )

    switch (attributeName) {
      case 'items':
      case 'see56CollectionHandle':
      case 'see56VariantIds':
        if (see56Enabled && this.scope.see56CollectionHandle) {
          this.#getSee56Recommendations()
        } else {
          this.getRecommendations()
        }
        break
    }
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
